"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Activity,
  BarChart3,
  Calendar,
  Camera,
  FileImage,
  Film,
  MoveLeft,
  Ruler,
  Star,
  Weight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Button,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from "@heroui/react";
import { Image } from "@heroui/image";
import { FormValues } from "@/models/Sila.Animals.schema.ts";
import { Link } from "react-router-dom";

import logo from "/public/logo.png";
import { PedigreeTree } from "@/features/Animals/Pedigree.tsx";
import MilkAnalysisTable from "@/features/Animals/MilkClorifdeTable.tsx";
import ReactPlayer from "react-player";
import { SilaAnimalsProfilePDF } from "@/pages/SilaAnimalsPdf.tsx";

interface AnimalProfileProps {
  animalData: FormValues;
  className?: string;
  // id: string;
}

export default function SilaAnimalsProfile({
  animalData,
  className = "",
}: AnimalProfileProps) {
  const [selectedImage, setSelectedImage] = useState(0);

  if (!animalData) {
    return <span>No Animal Data Yet</span>;
  }

  // Place this at the top of your component or just before rendering
  // const vaccinations: string[] = Array.isArray(animalData?.vaccinations)
  //   ? animalData.vaccinations.filter(Boolean)
  //   : typeof animalData?.vaccinations === "string"
  //     ? (animalData.vaccinations as string)
  //         .split(",")
  //         .map((v) => v.trim())
  //         .filter(Boolean)
  //     : [];

  console.log(animalData);
  // Performance metrics with descriptions
  const performanceMetricsData = [
    {
      key: "mw_me",
      label: "MW_ME",
      description: "Maternal Weight Maternal Effect",
      epd: animalData?.mw_me_epd ?? "",
      acc: animalData?.mw_me_acc ?? "",
      deca: animalData?.mw_me_deca ?? "",
    },
    {
      key: "bw_de",
      label: "BW_DE",
      description: "Birth Weight Direct Effect",
      epd: animalData?.bw_de_epd ?? "",
      acc: animalData?.bw_de_acc ?? "",
      deca: animalData?.bw_de_deca ?? "",
    },
    {
      key: "ww_de",
      label: "WW_DE",
      description: "Weaning Weight Direct Effect",
      epd: animalData?.ww_de_epd ?? "",
      acc: animalData?.ww_de_acc ?? "",
      deca: animalData?.ww_de_deca ?? "",
    },
    {
      key: "aw_de",
      label: "AW_DE",
      description: "Annual Weight Direct Effect",
      epd: animalData?.aw_de_epd ?? "",
      acc: animalData?.aw_de_acc ?? "",
      deca: animalData?.aw_de_deca ?? "",
    },
    {
      key: "yw_de",
      label: "YW_DE",
      description: "Yearling Weight Direct Effect",
      epd: animalData?.yw_de_epd ?? "",
      acc: animalData?.yw_de_acc ?? "",
      deca: animalData?.yw_de_deca ?? "",
    },
    {
      key: "sc365",
      label: "SC365",
      description: "Scrotal Circumference at 365 days",
      epd: animalData?.sc365_epd ?? "",
      acc: animalData?.sc365_acc ?? "",
      deca: animalData?.sc365_deca ?? "",
    },
    {
      key: "sc450",
      label: "SC450",
      description: "Scrotal Circumference at 450 days",
      epd: animalData?.sc450_epd ?? "",
      acc: animalData?.sc450_acc ?? "",
      deca: animalData?.sc450_deca ?? "",
    },
    {
      key: "bs",
      label: "BS",
      description: "Body Structure",
      epd: animalData?.bs_epd ?? "",
      acc: animalData?.bs_acc ?? "",
      deca: animalData?.bs_deca ?? "",
    },
    {
      key: "prec",
      label: "PREC",
      description: "Precocity",
      epd: animalData?.prec_epd ?? "",
      acc: animalData?.prec_acc ?? "",
      deca: animalData?.prec_deca ?? "",
    },
    {
      key: "musc",
      label: "MUSC",
      description: "Muscularity",
      epd: animalData?.musc_epd ?? "",
      acc: animalData?.musc_acc ?? "",
      deca: animalData?.musc_deca ?? "",
    },
    {
      key: "rea",
      label: "REA",
      description: "Rib Eye Area",
      epd: animalData?.rea_epd ?? "",
      acc: animalData?.rea_acc ?? "",
      deca: animalData?.rea_deca ?? "",
    },
    {
      key: "finishing",
      label: "FINISHING",
      description: "Finishing Score",
      epd: animalData?.finishing_epd ?? "",
      acc: animalData?.finishing_acc ?? "",
      deca: animalData?.finishing_deca ?? "",
    },
    {
      key: "mar",
      label: "MAR",
      description: "Marbling Score",
      epd: animalData?.mar_epd ?? "",
      acc: animalData?.mar_acc ?? "",
      deca: animalData?.mar_deca ?? "",
    },
    {
      key: "iabcz",
      label: "iABCZ",
      description: "iABCZ Index",
      epd: animalData?.iabcz_epd ?? "",
      acc: animalData?.iabcz_acc ?? "",
      deca: animalData?.iabcz_deca ?? "",
    },
  ];

  // Body measurements data
  const bodyMeasurementsData = [
    {
      key: "length_of_rump",
      label: "Length of Rump",
      value: animalData?.length_of_rump,
      unit: "cm",
    },
    {
      key: "height_of_rump",
      label: "Height of Rump",
      value: animalData?.height_of_rump,
      unit: "cm",
    },
    {
      key: "lower_height",
      label: "Lower Height",
      value: animalData?.lower_height,
      unit: "cm",
    },
    {
      key: "back_height",
      label: "Back Height",
      value: animalData?.back_height,
      unit: "cm",
    },
    {
      key: "body_length",
      label: "Body Length",
      value: animalData?.body_length,
      unit: "cm",
    },
    {
      key: "rib_depth",
      label: "Rib Depth",
      value: animalData?.rib_depth,
      unit: "cm",
    },
    {
      key: "chest_perimeter",
      label: "Chest Perimeter",
      value: animalData?.chest_perimeter,
      unit: "cm",
    },
    {
      key: "body_width",
      label: "Body Width",
      value: animalData?.body_width,
      unit: "cm",
    },
    {
      key: "scrotal_circumference",
      label: "Scrotal Circumference",
      value: animalData?.scrotal_circumference,
      unit: "cm",
    },
  ];

  const milkData = [
    {
      milk_kg: animalData?.milkKg ?? 0,
      age1p: animalData?.age1P ?? 0,
      ebi: animalData?.ebi ?? 0,
      milk_acc: animalData?.milkAcc ?? 0,
      age_1p_acc: animalData.age1pAcc ?? 0,
      ebi_acc: animalData.ebiAcc ?? 0,
      beta_lac: animalData.betaLac ?? "N/A",
      kappa_c: animalData.kappaC ?? "N/A",
      beta_c: animalData.betaC ?? "N/A",
    },
  ];

  return (
    <div
      className={`font-[suse] h-screen break-after-avoid break-before-avoid break-inside-avoid  bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 px-4 ${className}`}
    >
      <div className={"z-[-1]"}>{/* <HeroBackground /> */}</div>
      <div className="max-w-7xl flex flex-col gap-10 mx-auto z-50">
        <div
          className={
            "w-full flex items-center justify-between sm:flex" +
            "sm:flex-col sm:items-center mb-8 border-b-2 border-[#cfc130ff]"
          }
        >
          <Image alt="Logo" className="h-20 z-50 " src={logo} />
          <h1 className={"font-semibold text-2xl text-[#015354ff]"}>
            Silagreen Genetics
          </h1>
          {/* <h1 className={"text-4xl text-gray-600 z-20"}>Sila Green Genetics</h1> */}
        </div>
        <div>
          <Link to={`/search-animal`}>
            <Button variant={"light"} startContent={<MoveLeft />}>
              Go Back
            </Button>
          </Link>
        </div>

        {/* Header Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-yellow-50 via-green-50 to-indigo-50 p-8 mb-8">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative">
              {/* <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white/30 shadow-xl z-50"> */}
              <Image
                loading={"lazy"}
                src={
                  animalData?.imageUrls
                    ? animalData?.imageUrls[0]
                    : "/placeholder.svg?height=128&width=128"
                }
                alt={animalData?.name}
                width={128}
                height={128}
                className="w-full h-full object-contain z-50"
              />
              {/* </div> */}
              <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-2 shadow-lg">
                <Star className="w-5 h-5 text-yellow-800" />
              </div>
            </div>

            <div className="w-full flex-1 text-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className={"w-full flex items-center justify-between"}>
                  <h1 className="text-2x font-bold">{animalData?.name}</h1>
                  {/* <img alt="Logo" className="h-[50px] z-20 " src={logo} /> */}
                </div>
                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                  {animalData?.animalType} type
                </Badge>
              </div>
              <p className="text-xl text-gray-800 mb-4 ">
                DGR: {animalData?.dgr}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm opacity-90">Age</span>
                  </div>
                  <p className="font-semibold">{animalData?.age} months</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4" />
                    <span className="text-sm opacity-90">Weight</span>
                  </div>
                  <p className="font-semibold">{animalData?.weight}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4" />
                    <span className="text-sm opacity-90">Sex</span>
                  </div>
                  <p className="font-semibold">{animalData?.sex}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4" />
                    <span className="text-sm opacity-90">Birth Date</span>
                  </div>
                  <p className="font-semibold">{animalData?.birthDate}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4" />
                    <span className="text-sm opacity-90">Size</span>
                  </div>
                  <p className="font-semibold">{animalData?.size}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4" />
                    <span className="text-sm opacity-90">Coat Color</span>
                  </div>
                  <p className="font-semibold">{animalData?.coatColor}</p>
                </div>
                {/* <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"> */}
                {/*   <div className="flex items-center gap-2 mb-1"> */}
                {/*     <TrendingUp className="w-4 h-4" /> */}
                {/*     <span className="text-sm opacity-90">ADG</span> */}
                {/*   </div> */}
                {/*   <p className="font-semibold">{animalData?.adg}</p> */}
                {/* </div> */}
                {/* <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"> */}
                {/*   <div className="flex items-center gap-2 mb-1"> */}
                {/*     <Award className="w-4 h-4" /> */}
                {/*     <span className="text-sm opacity-90">Efficiency</span> */}
                {/*   </div> */}
                {/*   <p className="font-semibold"> */}
                {/*     {animalData?.indexEfficiency}% */}
                {/*   </p> */}
                {/* </div> */}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <Tabs
            variant={"light"}
            classNames={{
              tabList:
                "gap-6 w-full relative rounded-none p-4 border-b text-gray-800",
              cursor: "bg-[#008c8fff]",
              tab:
                "flex items-center gap-2 px-4 text-gray-800 py-2" +
                " text-sm" +
                " font-bold transition-colors hover:bg-gray-50 focus:bg-gray-100 focus:outline-none",
              tabContent:
                "text-gray-800 group-data-[selected=true]:text-[#cfc130ff]" +
                " font-medium",
            }}
          >
            <Tab
              key="photos"
              title={
                <div className="flex items-center space-x-2">
                  <FileImage />
                  <span>Photos</span>
                </div>
              }
            >
              <Card className="w-full mx-auto bg-white/80 backdrop-blur-sm border-white/20 shadow-xl z-50">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-cyan-900">
                    <Camera className="w-5 h-5" />
                    Photo Gallery
                  </CardTitle>
                </CardHeader>
                <CardContent className="mx-auto relative flex flex-col items-center justify-center p-6">
                  {animalData?.imageUrls && animalData?.imageUrls.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {/* Main Image */}
                      <div className="space-y-4">
                        <div className="relative  rounded-xl overflow-hidden shadow-lg">
                          <Image
                            src={
                              animalData?.imageUrls[selectedImage] ||
                              "/placeholder.svg?height=400&width=600"
                            }
                            // width={428}
                            // height={428}
                            alt={`${animalData?.name} - Image ${selectedImage + 1}`}
                            className=" transition-transform duration-300 hover:scale-105"
                          />
                        </div>

                        {/* Thumbnail Navigation */}
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {animalData?.imageUrls.map((url, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImage(index)}
                              className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                                selectedImage === index
                                  ? "border-blue-500 ring-2 ring-blue-200"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <img
                                src={
                                  url || "/placeholder.svg?height=80&width=80"
                                }
                                alt={`Thumbnail ${index + 1}`}
                                className="object-cover z-50"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Camera className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-500">
                        No images available for this animal
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Tab>
            <Tab
              title={
                <div className="flex items-center space-x-2">
                  <Film />
                  <span>Videos</span>
                </div>
              }
              key="videos"
            >
              {animalData.videos ? (
                animalData.videos.map((video) => (
                  <div className={"flex flex-col gap-2"}>
                    <ReactPlayer
                      slot="media"
                      key={video.url}
                      fallback={<img src={"./Placeholder-Bull.png"} />}
                      src={video.url}
                      controls={true}
                      light={
                        <img
                          src={animalData.imageUrls && animalData.imageUrls[0]}
                          className={"h-600px"}
                        />
                      }
                      style={{
                        width: "100%",
                        height: "700px",
                      }}
                    />
                    <Card className="p-4 shadow-amber-200 shadow-md bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                      <CardTitle className={"underline text-[#008c8fff] py-2"}>
                        {video.title}
                      </CardTitle>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {video.description}
                      </p>
                    </Card>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Camera className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">
                    No videos available for this animal
                  </p>
                </div>
              )}
            </Tab>
          </Tabs>
        </div>
        {/* Basic Information */}
        {animalData.bullType === "Beef Bull" && (
          <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-50 to-indigo-100 rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Activity className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* <div className="space-y-4"> */}
                {/*   <div> */}
                {/*     <label className="text-sm font-medium text-gray-600"> */}
                {/*       Sex */}
                {/*     </label> */}
                {/*     <p className="text-lg font-semibold text-gray-900"> */}
                {/*       {animalData?.sex} */}
                {/*     </p> */}
                {/*   </div> */}
                {/*   <div> */}
                {/*     <label className="text-sm font-medium text-gray-600"> */}
                {/*       Birth Date */}
                {/*     </label> */}
                {/*     <p className="text-lg font-semibold text-gray-900"> */}
                {/*       {animalData?.birthDate} */}
                {/*     </p> */}
                {/*   </div> */}
                {/*   <div> */}
                {/*     <label className="text-sm font-medium text-gray-600"> */}
                {/*       Coat Color */}
                {/*     </label> */}
                {/*     <p className="text-lg font-semibold text-gray-900"> */}
                {/*       {animalData?.coatColor} */}
                {/*     </p> */}
                {/*   </div> */}
                {/*   <div> */}
                {/*     <label className="text-sm font-medium text-gray-600"> */}
                {/*       Size */}
                {/*     </label> */}
                {/*     <p className="text-lg font-semibold text-gray-900"> */}
                {/*       {animalData?.size} */}
                {/*     </p> */}
                {/*   </div> */}
                {/* </div> */}

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      EPD Birth Weight
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {animalData?.birthWeight}
                    </p>
                  </div>
                  {/* <div> */}
                  {/*   <label className="text-sm font-medium text-gray-600"> */}
                  {/*     W120 */}
                  {/*   </label> */}
                  {/*   <p className="text-lg font-semibold text-gray-900"> */}
                  {/*     {animalData?.w120} */}
                  {/*   </p> */}
                  {/* </div> */}
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      EPD Weaning Weight
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {animalData?.ww}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      EPD Post Weaning
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {animalData?.pw}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      EPD Yearling Weight
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {animalData?.yw}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      PMWG
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {animalData?.pmwg}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Status
                    </label>
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      {animalData?.animalStatus}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Offspring
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {animalData?.numberOffspring}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {animalData.bullType === "Beef Bull" ? (
          <>
            {/* Performance Metrics Table */}
            <Card className="bg-white/80 backdrop-blur-sm border-white/20">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-green-900">
                  <BarChart3 className="w-5 h-5 text-[#008c8fff]" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table isStriped isCompact>
                    <TableHeader className="bg-gray-50/50">
                      <TableColumn className="font-semibold">
                        Metric
                      </TableColumn>
                      <TableColumn className="font-semibold text-center">
                        EPD
                      </TableColumn>
                      <TableColumn className="font-semibold text-center">
                        ACC
                      </TableColumn>
                      <TableColumn className="font-semibold text-center">
                        DECA
                      </TableColumn>
                      <TableColumn className="font-semibold">
                        Description
                      </TableColumn>
                    </TableHeader>
                    <TableBody>
                      {performanceMetricsData.map((metric) => (
                        <TableRow
                          key={metric.key}
                          className="hover:bg-blue-50/50 transition-colors"
                        >
                          <TableCell className="font-medium text-blue-900">
                            {metric.label}
                          </TableCell>
                          <TableCell className="font-semibold text-green-700 text-center">
                            {metric.epd !== undefined ? metric.epd : "N/A"}
                          </TableCell>
                          <TableCell className="font-semibold text-purple-700 text-center">
                            {metric.acc !== undefined ? metric.acc : "N/A"}
                          </TableCell>
                          <TableCell className="font-semibold text-orange-700 text-center">
                            {metric.deca !== undefined ? metric.deca : "N/A"}
                          </TableCell>
                          <TableCell className="text-gray-700">
                            {metric.description}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Body Measurements Table */}
            <Card className=" bg-white/80 backdrop-blur-sm border-white/20">
              <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-[#008c8fff]">
                  <Ruler className="w-5 h-5 " />
                  Body Measurements
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table isStriped isCompact>
                  <TableHeader className={"bg-[#008c8fff]"}>
                    {/* <TableHead className="bg-gray-50/50"> */}
                    <TableColumn className="font-semibold">
                      Measurement
                    </TableColumn>
                    <TableColumn className="font-semibold">Value</TableColumn>
                    <TableColumn className="font-semibold">Unit</TableColumn>
                    {/* </TableHead> */}
                  </TableHeader>
                  <TableBody>
                    {bodyMeasurementsData.map((measurement) => (
                      <TableRow
                        key={measurement.key}
                        className="hover:bg-purple-50/50 transition-colors"
                      >
                        <TableCell className="font-medium text-purple-900">
                          {measurement.label}
                        </TableCell>
                        <TableCell className="font-semibold text-indigo-700">
                          {measurement.value}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {measurement.unit}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <MilkAnalysisTable data={milkData} />
        )}

        {/* Breeding Information */}
        {/* <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl"> */}
        {/*   <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50 rounded-t-lg"> */}
        {/*     <CardTitle className="flex items-center gap-2 text-orange-900"> */}
        {/*       <Heart className="w-5 h-5" /> */}
        {/*       Breeding Information */}
        {/*     </CardTitle> */}
        {/*   </CardHeader> */}
        {/*   <CardContent className="p-6"> */}
        {/*     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"> */}
        {/*       <div className="space-y-4"> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Paternal Nucleus */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.paternalNucleus} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Paternal Type */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.paternalType} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Breeding Type */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.breedingType} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Racial Composition */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.racialComposition} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*       </div> */}

        {/*       <div className="space-y-4"> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Vaccination */}
        {/*           </label> */}
        {/*           <div className="flex flex-wrap gap-1 mt-1"> */}
        {/*             {vaccinations.length > 0 ? ( */}
        {/*               vaccinations.map((vaccine, idx) => ( */}
        {/*                 <Badge */}
        {/*                   key={vaccine + idx} */}
        {/*                   className="bg-green-100 text-green-800 border-green-200" */}
        {/*                 > */}
        {/*                   {vaccine} */}
        {/*                 </Badge> */}
        {/*               )) */}
        {/*             ) : ( */}
        {/*               <span className="text-gray-400 text-sm"> */}
        {/*                 No vaccinations */}
        {/*               </span> */}
        {/*             )} */}
        {/*           </div> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Birth Condition */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.birthCondition} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Insemination Program */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.inseminationProgram} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Phases */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.phases} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*       </div> */}

        {/*       <div className="space-y-4"> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Evaluation */}
        {/*           </label> */}
        {/*           <Badge className="bg-blue-100 text-blue-800 border-blue-200"> */}
        {/*             {animalData?.evaluation} */}
        {/*           </Badge> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Diagnosis */}
        {/*           </label> */}
        {/*           <Badge className="bg-green-100 text-green-800 border-green-200"> */}
        {/*             {animalData?.diagnosis} */}
        {/*           </Badge> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Status */}
        {/*           </label> */}
        {/*           <Badge className="bg-purple-100 text-purple-800 border-purple-200"> */}
        {/*             {animalData?.status} */}
        {/*           </Badge> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Slaughter Info */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.slaughterInfo} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </div> */}
        {/*   </CardContent> */}
        {/* </Card> */}

        {/* Genealogy */}
        {/* <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl"> */}
        {/*   <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-lg"> */}
        {/*     <CardTitle className="flex items-center gap-2 text-indigo-900"> */}
        {/*       <Users className="w-5 h-5" /> */}
        {/*       Genealogy */}
        {/*     </CardTitle> */}
        {/*   </CardHeader> */}
        {/*   <CardContent className="p-6"> */}
        {/*     <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> */}
        {/*       /!* Parents *!/ */}
        {/*       <div> */}
        {/*         <h3 className="text-lg font-semibold text-gray-900 mb-4"> */}
        {/*           Parents */}
        {/*         </h3> */}
        {/*         <div className="space-y-4"> */}
        {/*           <div className="bg-pink-50 p-4 rounded-lg border border-pink-200"> */}
        {/*             <h4 className="font-medium text-pink-900 mb-2"> */}
        {/*               Dam (Mother) */}
        {/*             </h4> */}
        {/*             <div className="space-y-1 text-sm"> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">Name:</span>{" "} */}
        {/*                 {animalData?.damName} */}
        {/*               </p> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">DGR:</span>{" "} */}
        {/*                 {animalData?.damDGR} */}
        {/*               </p> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">Breed:</span>{" "} */}
        {/*                 {animalData?.damBreed} */}
        {/*               </p> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">Birth Date:</span>{" "} */}
        {/*                 {animalData?.damBirthDate} */}
        {/*               </p> */}
        {/*             </div> */}
        {/*           </div> */}

        {/*           <div className="bg-blue-50 p-4 rounded-lg border border-blue-200"> */}
        {/*             <h4 className="font-medium text-blue-900 mb-2"> */}
        {/*               Sire (Father) */}
        {/*             </h4> */}
        {/*             <div className="space-y-1 text-sm"> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">Name:</span>{" "} */}
        {/*                 {animalData?.sireName} */}
        {/*               </p> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">DGR:</span>{" "} */}
        {/*                 {animalData?.sireDGR} */}
        {/*               </p> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">Breed:</span>{" "} */}
        {/*                 {animalData?.sireBreed} */}
        {/*               </p> */}
        {/*               <p> */}
        {/*                 <span className="font-medium">Birth Date:</span>{" "} */}
        {/*                 {animalData?.sireBirthDate} */}
        {/*               </p> */}
        {/*             </div> */}
        {/*           </div> */}
        {/*         </div> */}
        {/*       </div> */}

        {/*       /!* Grandparents *!/ */}
        {/*       <div> */}
        {/*         <h3 className="text-lg font-semibold text-gray-900 mb-4"> */}
        {/*           Grandparents */}
        {/*         </h3> */}
        {/*         <div className="space-y-4"> */}
        {/*           <div className="bg-purple-50 p-4 rounded-lg border border-purple-200"> */}
        {/*             <h4 className="font-medium text-purple-900 mb-2"> */}
        {/*               Paternal Grandparents */}
        {/*             </h4> */}
        {/*             <div className="grid grid-cols-1 gap-2 text-sm"> */}
        {/*               <div> */}
        {/*                 <p className="font-medium">Grand Dam:</p> */}
        {/*                 <p> */}
        {/*                   {animalData?.paternalGrandDamName} ( */}
        {/*                   {animalData?.paternalGrandDamDGR}) */}
        {/*                 </p> */}
        {/*                 <p className="text-gray-600"> */}
        {/*                   {animalData?.paternalGrandDamBreed} */}
        {/*                 </p> */}
        {/*               </div> */}
        {/*               <div> */}
        {/*                 <p className="font-medium">Grand Sire:</p> */}
        {/*                 <p> */}
        {/*                   {animalData?.paternalGrandSireName} ( */}
        {/*                   {animalData?.paternalGrandSireDGR}) */}
        {/*                 </p> */}
        {/*                 <p className="text-gray-600"> */}
        {/*                   {animalData?.paternalGrandSireBreed} */}
        {/*                 </p> */}
        {/*               </div> */}
        {/*             </div> */}
        {/*           </div> */}

        {/*           <div className="bg-green-50 p-4 rounded-lg border border-green-200"> */}
        {/*             <h4 className="font-medium text-green-900 mb-2"> */}
        {/*               Maternal Grandparents */}
        {/*             </h4> */}
        {/*             <div className="grid grid-cols-1 gap-2 text-sm"> */}
        {/*               <div> */}
        {/*                 <p className="font-medium">Grand Dam:</p> */}
        {/*                 <p> */}
        {/*                   {animalData?.maternalGrandDamName} ( */}
        {/*                   {animalData?.maternalGrandDamDGR}) */}
        {/*                 </p> */}
        {/*                 <p className="text-gray-600"> */}
        {/*                   {animalData?.maternalGrandDamBreed} */}
        {/*                 </p> */}
        {/*               </div> */}
        {/*               <div> */}
        {/*                 <p className="font-medium">Grand Sire:</p> */}
        {/*                 <p> */}
        {/*                   {animalData?.maternalGrandSireName} ( */}
        {/*                   {animalData?.maternalGrandSireDGR}) */}
        {/*                 </p> */}
        {/*                 <p className="text-gray-600"> */}
        {/*                   {animalData?.maternalGrandSireBreed} */}
        {/*                 </p> */}
        {/*               </div> */}
        {/*             </div> */}
        {/*           </div> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </div> */}
        {/*   </CardContent> */}
        {/* </Card> */}

        {/* Management Information */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
        {/*   /!* Farm Information *!/ */}
        {/*   <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl"> */}
        {/*     <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-t-lg"> */}
        {/*       <CardTitle className="flex items-center gap-2 text-yellow-900"> */}
        {/*         <MapPin className="w-5 h-5" /> */}
        {/*         Farm Information */}
        {/*       </CardTitle> */}
        {/*     </CardHeader> */}
        {/*     <CardContent className="p-6"> */}
        {/*       <div className="space-y-4"> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Farmer ID */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.farmerID} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Farm ID */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.farmID} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Farm Name */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.farm} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Assigned Supervisor */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.assignedSupervisor} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </CardContent> */}
        {/*   </Card> */}

        {/*   /!* Health & Management *!/ */}
        {/*   <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl"> */}
        {/*     <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-t-lg"> */}
        {/*       <CardTitle className="flex items-center gap-2 text-teal-900"> */}
        {/*         <Stethoscope className="w-5 h-5" /> */}
        {/*         Health & Management */}
        {/*       </CardTitle> */}
        {/*     </CardHeader> */}
        {/*     <CardContent className="p-6"> */}
        {/*       <div className="space-y-4"> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Cattle Class */}
        {/*           </label> */}
        {/*           <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200"> */}
        {/*             {animalData?.cattleClass} */}
        {/*           </Badge> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Current Phase */}
        {/*           </label> */}
        {/*           <p className="text-lg font-semibold text-gray-900"> */}
        {/*             {animalData?.phases} */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Health Status */}
        {/*           </label> */}
        {/*           <Badge className="bg-green-100 text-green-800 border-green-200"> */}
        {/*             {animalData?.diagnosis} */}
        {/*           </Badge> */}
        {/*         </div> */}
        {/*         <div> */}
        {/*           <label className="text-sm font-medium text-gray-600"> */}
        {/*             Overall Evaluation */}
        {/*           </label> */}
        {/*           <Badge className="bg-blue-100 text-blue-800 border-blue-200"> */}
        {/*             {animalData?.evaluation} */}
        {/*           </Badge> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </CardContent> */}
        {/*   </Card> */}
        {/* </div> */}

        <div className={".avoidBreak top-24"}>
          <Card>
            <CardHeader className={"bg-[#008c8fff] rounded-md"}>
              <CardTitle className={"text-[#cfc130ff]"}>
                Animal Lineage Pedigree
              </CardTitle>
            </CardHeader>
            <CardContent className={"py-4"}>
              <PedigreeTree animal={animalData} />
            </CardContent>
          </Card>
        </div>
        {/* </TabsContent> */}

        {/* <TabsContent value="media" className="space-y-6"> */}
        {/*   /!* Image Gallery *!/ */}
        {/*   <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl"> */}
        {/*     <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-lg"> */}
        {/*       <CardTitle className="flex items-center gap-2 text-cyan-900"> */}
        {/*         <Camera className="w-5 h-5" /> */}
        {/*         Photo Gallery */}
        {/*       </CardTitle> */}
        {/*     </CardHeader> */}
        {/*     <CardContent className="p-6"> */}
        {/*       {animalData?.imageUrls && animalData?.imageUrls.length > 0 ? ( */}
        {/*         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
        {/*           /!* Main Image *!/ */}
        {/*           <div className="space-y-4"> */}
        {/*             <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg"> */}
        {/*               <Image */}
        {/*                 src={ */}
        {/*                   animalData?.imageUrls[selectedImage] || */}
        {/*                   "/placeholder.svg?height=400&width=600" */}
        {/*                 } */}
        {/*                 alt={`${animalData?.name} - Image ${selectedImage + 1}`} */}
        {/*                 className="object-cover transition-transform duration-300 hover:scale-105" */}
        {/*               /> */}
        {/*             </div> */}

        {/*             /!* Thumbnail Navigation *!/ */}
        {/*             <div className="flex gap-2 overflow-x-auto pb-2"> */}
        {/*               {animalData?.imageUrls.map((url, index) => ( */}
        {/*                 <button */}
        {/*                   key={index} */}
        {/*                   onClick={() => setSelectedImage(index)} */}
        {/*                   className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${ */}
        {/*                     selectedImage === index */}
        {/*                       ? "border-blue-500 ring-2 ring-blue-200" */}
        {/*                       : "border-gray-200 hover:border-gray-300" */}
        {/*                   }`} */}
        {/*                 > */}
        {/*                   <Image */}
        {/*                     src={url || "/placeholder.svg?height=80&width=80"} */}
        {/*                     alt={`Thumbnail ${index + 1}`} */}
        {/*                     className="object-cover" */}
        {/*                   /> */}
        {/*                 </button> */}
        {/*               ))} */}
        {/*             </div> */}
        {/*           </div> */}

        {/*           /!* Image Details *!/ */}
        {/*           <div className="space-y-4"> */}
        {/*             <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100"> */}
        {/*               <h3 className="text-lg font-semibold text-blue-900 mb-4"> */}
        {/*                 Image Details */}
        {/*               </h3> */}
        {/*               <div className="space-y-3"> */}
        {/*                 <div> */}
        {/*                   <label className="text-sm font-medium text-gray-600"> */}
        {/*                     Image */}
        {/*                   </label> */}
        {/*                   <p className="text-lg font-semibold text-gray-900"> */}
        {/*                     {selectedImage + 1} of{" "} */}
        {/*                     {animalData?.imageUrls.length} */}
        {/*                   </p> */}
        {/*                 </div> */}
        {/*                 <div> */}
        {/*                   <label className="text-sm font-medium text-gray-600"> */}
        {/*                     Animal */}
        {/*                   </label> */}
        {/*                   <p className="text-lg font-semibold text-gray-900"> */}
        {/*                     {animalData?.name} */}
        {/*                   </p> */}
        {/*                 </div> */}
        {/*                 <div> */}
        {/*                   <label className="text-sm font-medium text-gray-600"> */}
        {/*                     DGR */}
        {/*                   </label> */}
        {/*                   <p className="text-lg font-semibold text-gray-900"> */}
        {/*                     {animalData?.dgr} */}
        {/*                   </p> */}
        {/*                 </div> */}
        {/*               </div> */}
        {/*             </div> */}

        {/*             <div className="flex gap-2"> */}
        {/*               <Button className="flex-1 bg-blue-600 hover:bg-blue-700"> */}
        {/*                 <Download className="w-4 h-4 mr-2" /> */}
        {/*                 Download */}
        {/*               </Button> */}
        {/*               <Button variant="outline" className="flex-1"> */}
        {/*                 <Share2 className="w-4 h-4 mr-2" /> */}
        {/*                 Share */}
        {/*               </Button> */}
        {/*             </div> */}
        {/*           </div> */}
        {/*         </div> */}
        {/*       ) : ( */}
        {/*         <div className="text-center py-12"> */}
        {/*           <Camera className="w-16 h-16 mx-auto text-gray-400 mb-4" /> */}
        {/*           <p className="text-gray-500"> */}
        {/*             No images available for this animal */}
        {/*           </p> */}
        {/*         </div> */}
        {/*       )} */}
        {/*     </CardContent> */}
        {/*   </Card> */}

        {/*   /!* Video Gallery *!/ */}
        {/*   <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl"> */}
        {/*     <CardHeader className="bg-gradient-to-r from-red-50 to-pink-50 rounded-t-lg"> */}
        {/*       <CardTitle className="flex items-center gap-2 text-red-900"> */}
        {/*         <Play className="w-5 h-5" /> */}
        {/*         Video Gallery */}
        {/*       </CardTitle> */}
        {/*     </CardHeader> */}
        {/*     <CardContent className="p-6"> */}
        {/*       {animalData?.videos && animalData?.videos.length > 0 ? ( */}
        {/*         <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> */}
        {/*           {animalData?.videos && */}
        {/*             animalData?.videos.map(() => ( */}
        {/*               // use videos there */}
        {/*               <div */}
        {/*                 // key={index} */}
        {/*                 className="relative aspect-video rounded-xl overflow-hidden shadow-lg bg-gray-100" */}
        {/*               > */}
        {/*                 <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900"> */}
        {/*                   <div className="text-center text-white"> */}
        {/*                     <Play className="w-16 h-16 mx-auto mb-4 opacity-80" /> */}
        {/*                     <p className="text-lg font-medium"> */}
        {/*                       /!* Video {index + 1} *!/ */}
        {/*                     </p> */}
        {/*                     <p className="text-sm opacity-80"> */}
        {/*                       {animalData?.name} */}
        {/*                     </p> */}
        {/*                   </div> */}
        {/*                 </div> */}
        {/*                 <Button */}
        {/*                   className="absolute bottom-4 right-4 bg-white/20 backdrop-blur-sm hover:bg-white/30" */}
        {/*                   size="sm" */}
        {/*                 > */}
        {/*                   <Play className="w-4 h-4 mr-2" /> */}
        {/*                   Play */}
        {/*                 </Button> */}
        {/*               </div> */}
        {/*             ))} */}
        {/*         </div> */}
        {/*       ) : ( */}
        {/*         <div className="text-center py-12"> */}
        {/*           <Play className="w-16 h-16 mx-auto text-gray-400 mb-4" /> */}
        {/*           <p className="text-gray-500"> */}
        {/*             No videos available for this animal */}
        {/*           </p> */}
        {/*         </div> */}
        {/*       )} */}
        {/*     </CardContent> */}
        {/*   </Card> */}
        {/* </TabsContent> */}
        {/* </Tabs> */}
        <SilaAnimalsProfilePDF {...animalData} />
      </div>
    </div>
  );
}
